import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Table, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from "styled-components";
import { HeaderTitle, Colors, WhiteTableContainer, PrimaryButton, DangerButton, SecondaryButton } from "../../../utils/budget-tool-specific";
import { GetDB, generateGUID } from "../../../utils/database/firebase-budget-tool";
import firebase from "firebase/compat/app";

const Status = (props) => {
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedError, setSelectedError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [errorToDelete, setErrorToDelete] = useState(null);

    useEffect(() => {
        loadErrors();
    }, []);

    const loadErrors = async () => {
        try {
            setLoading(true);
            const db = GetDB();
            
            if (!db) {
                console.error('Database connection not available');
                setErrors([]);
                return;
            }
            
            const snapshot = await db.collection('errors')
                .orderBy('timestamp', 'desc')
                .limit(100)
                .get();
            
            const errorList = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    // Ensure we have fallback values for critical fields
                    timestamp: data.timestamp || null,
                    application: data.application || 'Unknown',
                    errorType: data.errorType || 'Unknown',
                    severity: data.severity || 'unknown',
                    error: data.error || {}
                };
            });
            
            setErrors(errorList);
        } catch (error) {
            console.error('Error loading errors:', error);
            // Don't clear existing errors on fetch failure
            if (errors.length === 0) {
                setErrors([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const deleteError = async (errorId) => {
        try {
            if (!errorId) {
                console.error('No error ID provided for deletion');
                return;
            }
            
            const db = GetDB();
            if (!db) {
                console.error('Database connection not available');
                return;
            }
            
            await db.collection('errors').doc(errorId).delete();
            setErrors(errors.filter(error => error.id !== errorId));
            setDeleteModalOpen(false);
            setErrorToDelete(null);
        } catch (error) {
            console.error('Error deleting error:', error);
            // Keep modal open on error so user can try again
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'danger';
            case 'error': return 'warning';
            case 'warning': return 'info';
            case 'info': return 'light';
            default: return 'secondary';
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Unknown';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleString();
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const openErrorDetails = (error) => {
        setSelectedError(error);
        setModalOpen(true);
    };

    const openDeleteModal = (error) => {
        setErrorToDelete(error);
        setDeleteModalOpen(true);
    };

    const createTestError = async () => {
        try {
            const db = GetDB();
            
            const testErrors = [
                {
                    application: 'budget-calculator',
                    errorType: 'api-request-failed',
                    error: {
                        message: 'Failed to fetch flight data from API',
                        name: 'NetworkError',
                        stack: 'NetworkError: Failed to fetch\n    at fetchFlightData (flight-api.js:23:15)\n    at async calculateBudget (budget-tool.js:145:8)',
                        status: 500,
                        text: 'Internal Server Error'
                    },
                    severity: 'error'
                },
                {
                    application: 'experience-planner',
                    errorType: 'form-validation-failed',
                    error: {
                        message: 'Required field missing: destination',
                        name: 'ValidationError',
                        stack: null,
                        status: null,
                        text: null
                    },
                    severity: 'warning'
                },
                {
                    application: 'budget-calculator',
                    errorType: 'calculation-error',
                    error: {
                        message: 'Cannot calculate total cost: invalid currency conversion rate',
                        name: 'CalculationError',
                        stack: 'CalculationError: Invalid conversion rate\n    at convertCurrency (currency.js:45:12)\n    at calculateTotal (budget.js:89:20)',
                        status: null,
                        text: null
                    },
                    severity: 'critical'
                }
            ];

            const randomError = testErrors[Math.floor(Math.random() * testErrors.length)];
            
            const errorDocument = {
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                application: randomError.application,
                errorType: randomError.errorType,
                sessionId: generateGUID(),
                
                error: randomError.error,
                
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                language: navigator.language,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                viewportSize: `${window.innerWidth}x${window.innerHeight}`,
                
                url: window.location.href,
                referrer: document.referrer,
                formData: {
                    destination: randomError.application === 'experience-planner' ? null : 'Toronto',
                    attendees: '25',
                    email: '[REDACTED]',
                    name: '[REDACTED]'
                },
                calculationResults: randomError.application === 'budget-calculator' ? {
                    total: 125000,
                    perPerson: 5000,
                    currency: 'CAD$',
                    attendees: 25
                } : null,
                
                userEmail: '[REDACTED]',
                severity: randomError.severity,
                environment: getEnvironment()
            };
            
            await db.collection('errors').add(errorDocument);
            loadErrors(); // Refresh the list
            
        } catch (error) {
            console.error('Failed to create test error:', error);
        }
    };

    const getEnvironment = () => {
        const loc = window.location.href;
        if (loc.includes("localhost") || loc.includes("test.deleptual") || loc.includes("deleptual")) {
            return "dev";
        } else if (loc.includes("moniker-virtual")) {
            return "production";
        }
        return "dev";
    };

    return (
        <PageContainer>
            <Container fluid>
                <HeaderTitle>Error Status Dashboard</HeaderTitle>
                    
                    <WhiteTableContainer>
                        <Row className="mb-3">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5>Recent Errors ({errors.length})</h5>
                                    <div>
                                        <SecondaryButton onClick={createTestError} className="me-2">
                                            <FontAwesomeIcon icon="plus" /> Add Test Error
                                        </SecondaryButton>
                                        <PrimaryButton onClick={loadErrors} disabled={loading}>
                                            <FontAwesomeIcon icon="sync" spin={loading} /> Refresh
                                        </PrimaryButton>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {loading ? (
                            <Row>
                                <Col className="text-center">
                                    <FontAwesomeIcon icon="sync" spin /> Loading errors...
                                </Col>
                            </Row>
                        ) : errors.length === 0 ? (
                            <Row>
                                <Col className="text-center">
                                    <p>No errors found!</p>
                                </Col>
                            </Row>
                        ) : (
                            <ErrorTable striped hover>
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Application</th>
                                        <th>Error Type</th>
                                        <th>Severity</th>
                                        <th>Message</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {errors.map((error) => (
                                        <tr key={error.id || Math.random()}>
                                            <td>{formatTimestamp(error.timestamp)}</td>
                                            <td>{error.application || 'Unknown App'}</td>
                                            <td>{error.errorType || 'Unknown Type'}</td>
                                            <td>
                                                <Badge color={getSeverityColor(error.severity)}>
                                                    {error.severity || 'unknown'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <TruncatedMessage>
                                                    {error.error?.message || error.message || 'No message available'}
                                                </TruncatedMessage>
                                            </td>
                                            <td>
                                                <ActionButton 
                                                    size="sm" 
                                                    color="info" 
                                                    className="me-2"
                                                    onClick={() => openErrorDetails(error)}
                                                >
                                                    <FontAwesomeIcon icon="eye" />
                                                </ActionButton>
                                                <ActionButton 
                                                    size="sm" 
                                                    color="danger"
                                                    onClick={() => openDeleteModal(error)}
                                                >
                                                    <FontAwesomeIcon icon="trash" />
                                                </ActionButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </ErrorTable>
                        )}
                    </WhiteTableContainer>

                    {/* Error Details Modal */}
                    <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                        <ModalHeader toggle={() => setModalOpen(false)}>
                            Error Details
                        </ModalHeader>
                        <ModalBody>
                            {selectedError && (
                                <div>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>Timestamp:</strong></Col>
                                        <Col>{formatTimestamp(selectedError.timestamp)}</Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>Application:</strong></Col>
                                        <Col>{selectedError.application || 'Unknown'}</Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>Error Type:</strong></Col>
                                        <Col>{selectedError.errorType || 'Unknown'}</Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>Severity:</strong></Col>
                                        <Col>
                                            <Badge color={getSeverityColor(selectedError.severity)}>
                                                {selectedError.severity || 'unknown'}
                                            </Badge>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>Session ID:</strong></Col>
                                        <Col><code>{selectedError.sessionId || 'Not available'}</code></Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>URL:</strong></Col>
                                        <Col>{selectedError.url || 'Not available'}</Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>User Agent:</strong></Col>
                                        <Col><small>{selectedError.userAgent || 'Not available'}</small></Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>Error Message:</strong></Col>
                                        <Col>{selectedError.error?.message || selectedError.message || 'No message available'}</Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col sm="3"><strong>Error Name:</strong></Col>
                                        <Col>{selectedError.error?.name || 'Unknown'}</Col>
                                    </Row>
                                    {selectedError.error?.status && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>HTTP Status:</strong></Col>
                                            <Col><Badge color="secondary">{selectedError.error.status}</Badge></Col>
                                        </Row>
                                    )}
                                    {selectedError.error?.stack && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>Stack Trace:</strong></Col>
                                            <Col>
                                                <StackTrace>
                                                    {selectedError.error.stack}
                                                </StackTrace>
                                            </Col>
                                        </Row>
                                    )}
                                    {selectedError.platform && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>Platform:</strong></Col>
                                            <Col>{selectedError.platform}</Col>
                                        </Row>
                                    )}
                                    {selectedError.screenResolution && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>Screen Resolution:</strong></Col>
                                            <Col>{selectedError.screenResolution}</Col>
                                        </Row>
                                    )}
                                    {selectedError.viewportSize && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>Viewport Size:</strong></Col>
                                            <Col>{selectedError.viewportSize}</Col>
                                        </Row>
                                    )}
                                    {selectedError.environment && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>Environment:</strong></Col>
                                            <Col><Badge color="info">{selectedError.environment}</Badge></Col>
                                        </Row>
                                    )}
                                    {selectedError.formData && Object.keys(selectedError.formData).length > 0 && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>Form Data:</strong></Col>
                                            <Col>
                                                <FormDataContainer>
                                                    {JSON.stringify(selectedError.formData, null, 2)}
                                                </FormDataContainer>
                                            </Col>
                                        </Row>
                                    )}
                                    {selectedError.calculationResults && Object.keys(selectedError.calculationResults).length > 0 && (
                                        <Row className="mb-3">
                                            <Col sm="3"><strong>Calculation Results:</strong></Col>
                                            <Col>
                                                <FormDataContainer>
                                                    {JSON.stringify(selectedError.calculationResults, null, 2)}
                                                </FormDataContainer>
                                            </Col>
                                        </Row>
                                    )}
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => setModalOpen(false)}>
                                Close
                            </Button>
                        </ModalFooter>
                    </Modal>

                    {/* Delete Confirmation Modal */}
                    <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
                        <ModalHeader toggle={() => setDeleteModalOpen(false)}>
                            Confirm Delete
                        </ModalHeader>
                        <ModalBody>
                            Are you sure you want to delete this error? This action cannot be undone.
                            {errorToDelete && (
                                <div className="mt-3">
                                    <strong>Error:</strong> {errorToDelete.error?.message || errorToDelete.message || 'Unknown error'}
                                    <br />
                                    <strong>Application:</strong> {errorToDelete.application || 'Unknown'}
                                    <br />
                                    <strong>Type:</strong> {errorToDelete.errorType || 'Unknown'}
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
                                Cancel
                            </Button>
                            <DangerButton onClick={() => deleteError(errorToDelete.id)}>
                                Delete
                            </DangerButton>
                        </ModalFooter>
                    </Modal>
                </Container>
        </PageContainer>
    );
};

export default Status;

const PageContainer = styled.div`
    padding: 30px 15px;
    height: 100%;
    box-sizing: border-box;
`;

const ErrorTable = styled(Table)`
    font-size: 14px;
`;

const TruncatedMessage = styled.div`
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActionButton = styled(Button)`
    margin-right: 5px;
    padding: 5px 8px;
`;

const StackTrace = styled.pre`
    background-color: #f8f9fa;
    padding: 10px;
    border-radius: 4px;
    font-size: 12px;
    max-height: 200px;
    overflow-y: auto;
`;

const FormDataContainer = styled.pre`
    background-color: #f8f9fa;
    padding: 10px;
    border-radius: 4px;
    font-size: 12px;
    max-height: 150px;
    overflow-y: auto;
    white-space: pre-wrap;
`;